import { readPunchlist } from "./punchlist.mjs";
import { createClient } from "@supabase/supabase-js";
import { createPrivateMediaClient } from "./privateMedia.mjs";
import { jobDayRange } from "./jobDayRange.mjs";
import { prepareJobCostCodeMutation, applyJobCostCodeMutation } from "./jobCostCodeMutation.mjs";
import { aggregateProductionRates } from "./productionRates.mjs";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    "Missing Supabase environment variables. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY.",
  );
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Guard: prevent "all" or invalid org_id from reaching the database
function validateOrgId(orgId, context) {
  if (!orgId || orgId === "all") {
    throw new Error(`Invalid org_id "${orgId}" in ${context} — must be a valid UUID`);
  }
}

// Format a Date to YYYY-MM-DD using local timezone (not UTC)
// Avoids the toISOString().split("T")[0] bug where dates shift near midnight
export function toLocalDateStr(date) {
  if (typeof date === "string") return date;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const PM4SUBS_API_URL = process.env.REACT_APP_PM4SUBS_API_URL || "";
const media = createPrivateMediaClient({
  getSession: () => supabase.auth.getSession(),
  fetchImpl: (...args) => fetch(...args),
  createObjectURL: blob => URL.createObjectURL(blob),
  revokeObjectURL: url => URL.revokeObjectURL(url),
  pmApiUrl: PM4SUBS_API_URL,
});

export const getPhotoUrl = media.getPhotoUrl;
export const getPresignedPhotoUrl = media.getPresignedPhotoUrl;
export const setMediaSession = media.setSession;
export const invalidateMedia = media.invalidate;

// ============ Retry Helper ============

/**
 * Retry a function up to maxRetries times with exponential backoff.
 * Only use for read operations — never for mutations.
 */


// ============ Timezone Helpers ============

/**
 * Get timezone from GPS longitude (US-focused)
 * Uses approximate timezone boundaries for continental US
 * @param {number} longitude - GPS longitude (negative for US)
 * @param {number} latitude - GPS latitude (optional, for edge cases)
 * @returns {string} IANA timezone identifier
 */
export function getTimezoneFromCoordinates(longitude, latitude = null) {
  if (!longitude && longitude !== 0) return "America/Denver"; // Default to Mountain

  const lng = parseFloat(longitude);

  // US timezone boundaries (approximate)
  // Pacific: west of -115 (but parts of Nevada/Idaho are Mountain)
  // Mountain: -115 to -102
  // Central: -102 to -87
  // Eastern: east of -87

  if (lng <= -115) {
    return "America/Los_Angeles"; // Pacific
  } else if (lng <= -102) {
    return "America/Denver"; // Mountain
  } else if (lng <= -87) {
    return "America/Chicago"; // Central
  } else {
    return "America/New_York"; // Eastern
  }
}

/**
 * Get timezone from US state abbreviation or name
 * @param {string} state - State abbreviation (e.g., "WY") or full name
 * @returns {string} IANA timezone identifier
 */
export function getTimezoneFromState(state) {
  if (!state) return "America/Denver"; // Default to Mountain Time

  const s = state.trim().toUpperCase();

  // Pacific Time states
  if (
    s === "OR" ||
    s === "OREGON" ||
    s === "WA" ||
    s === "WASHINGTON" ||
    s === "CA" ||
    s === "CALIFORNIA" ||
    s === "NV" ||
    s === "NEVADA"
  ) {
    return "America/Los_Angeles";
  }

  // Arizona does not observe DST
  if (s === "AZ" || s === "ARIZONA") {
    return "America/Phoenix";
  }

  // Mountain Time states (most of your jobs)
  if (
    s === "WY" ||
    s === "WYOMING" ||
    s === "MT" ||
    s === "MONTANA" ||
    s === "CO" ||
    s === "COLORADO" ||
    s === "UT" ||
    s === "UTAH" ||
    s === "NM" ||
    s === "NEW MEXICO"
  ) {
    return "America/Denver";
  }

  // Idaho is split - Boise area is Mountain
  if (s === "ID" || s === "IDAHO") {
    return "America/Boise";
  }

  // Default to Mountain Time for unknown states
  return "America/Denver";
}

/**
 * Get timezone - tries coordinates first, then state, then default
 * @param {number} longitude - GPS longitude
 * @param {number} latitude - GPS latitude
 * @param {string} state - State abbreviation or name (fallback)
 * @returns {string} IANA timezone identifier
 */
export function getTimezone(longitude, latitude, state) {
  // Prefer coordinates if available
  if (longitude) {
    return getTimezoneFromCoordinates(longitude, latitude);
  }
  // Fall back to state
  if (state) {
    return getTimezoneFromState(state);
  }
  // Default
  return "America/Denver";
}

// ============ Jobs ============

export async function getJobs(orgId) {
  const rows = [], seen = new Set();
  let afterId = null;
  for (;;) {
    // Immutable IDs avoid offset skips when earlier jobs are renamed or deleted.
    let query = supabase.from("t4s_jobs").select("*").order("id").limit(500);
    if (afterId) query = query.gt("id", afterId);
    if (orgId !== "all") query = query.eq("org_id", orgId);
    const { data, error } = await query;
    if (error) throw error;
    for (const row of data || []) {
      if (!row.id || seen.has(row.id) || (afterId && row.id <= afterId)) throw new Error("Jobs changed while loading. Refresh before continuing.");
      seen.add(row.id); rows.push(row);
    }
    if ((data || []).length < 500) return rows.sort((a, b) => (a.name || "").localeCompare(b.name || "") || a.id.localeCompare(b.id));
    afterId = data[data.length - 1].id;
  }
}

export async function getJob(jobId) {
  const { data, error } = await supabase
    .from("t4s_jobs")
    .select("*")
    .eq("id", jobId)
    .single();

  if (error) {
    console.error("Error fetching job:", error);
    throw error;
  }
  return data;
}

export async function createJob(jobData) {
  validateOrgId(jobData.org_id, "createJob");
  // Auto-set timezone from GPS coordinates or state if not provided
  const dataWithTimezone = {
    ...jobData,
    timezone:
      jobData.timezone ||
      getTimezone(jobData.longitude, jobData.latitude, jobData.state),
  };

  const { data, error } = await supabase
    .from("t4s_jobs")
    .insert(dataWithTimezone)
    .select()
    .single();

  if (error) {
    console.error("Error creating job:", error);
    throw error;
  }
  return data;
}

export async function updateJob(jobId, updates) {
  // Ordinary edits must not silently reinterpret historical punches. Timezone
  // changes are explicit, not re-inferred from an often-unchanged state field.
  const updatesWithTimezone = { ...updates };
  if (Object.prototype.hasOwnProperty.call(updates, "timezone")) {
    if (!updates.timezone) throw new Error("Choose a valid job timezone.");
    try { new Intl.DateTimeFormat("en-US", { timeZone: updates.timezone }); }
    catch { throw new Error("Choose a valid job timezone."); }
  }

  const { data, error } = await supabase
    .from("t4s_jobs")
    .update(updatesWithTimezone)
    .eq("id", jobId)
    .select()
    .single();

  if (error) {
    console.error("Error updating job:", error);
    throw error;
  }
  return data;
}

// ============ Employees ============

export async function getEmployees(orgId, activeOnly = true) {
  if (!orgId) throw new Error("Select a company before loading employees.");
  const read = async (linked = false) => {
    const rows = [], seen = new Set();
    for (let from = 0; ; from += 500) {
      let query = supabase.from("t4s_employees").select(
        linked ? "*, t4s_employee_orgs!inner(org_id, is_primary), trade:t4s_trades(*)" : "*, t4s_employee_orgs(org_id, is_primary), trade:t4s_trades(*)");
      if (orgId !== "all") query = query.eq(linked ? "t4s_employee_orgs.org_id" : "org_id", orgId);
      if (activeOnly) query = query.eq("is_active", true);
      const { data, error } = await query.order("last_name").order("id").range(from, from + 499);
      if (error) throw error;
      for (const row of data || []) {
        if (seen.has(row.id)) throw new Error("Employees changed while loading. Refresh before continuing.");
        seen.add(row.id); rows.push(row);
      }
      if ((data || []).length < 500) return rows;
    }
  };
  const direct = await read();
  const linked = orgId === "all" ? [] : await read(true);
  const merged = new Map([...direct, ...linked].map(row => [row.id, row]));
  return [...merged.values()].map(employee => ({ ...employee,
    trade_name: employee.trade?.name || null, trade_color: employee.trade?.color || null,
    trade_bg_light: employee.trade?.bg_light || null,
  })).sort((a, b) => (a.last_name || "").localeCompare(b.last_name || "") || a.id.localeCompare(b.id));
}

export async function getEmployeesForImport(orgId, expectedOwnerId) {
  validateOrgId(orgId, "getEmployeesForImport");
  await employeeAdminOwner(expectedOwnerId);
  const data = await getEmployees(orgId, false);
  await employeeAdminOwner(expectedOwnerId);
  return data;
}

export async function getCostCodesForImport(orgId, expectedOwnerId) {
  validateOrgId(orgId, "getCostCodesForImport");
  await employeeAdminOwner(expectedOwnerId);
  const rows = [], seen = new Set();
  for (let from = 0; ; from += 500) {
    const { data, error } = await supabase.from("t4s_cost_codes").select("*")
      .eq("org_id", orgId).order("code").order("id").range(from, from + 499);
    await employeeAdminOwner(expectedOwnerId);
    if (error) throw error;
    for (const row of data || []) {
      if (seen.has(row.id)) throw new Error("Cost codes changed while loading. Refresh before importing.");
      seen.add(row.id); rows.push(row);
    }
    if ((data || []).length < 500) return rows;
  }
}

export async function getEmployee(employeeId) {
  const { data, error } = await supabase
    .from("t4s_employees")
    .select("*")
    .eq("id", employeeId)
    .single();

  if (error) {
    console.error("Error fetching employee:", error);
    throw error;
  }
  return data;
}

// ============ Cost Codes ============

export async function getCostCodes(orgId) {
  let query = supabase
    .from("t4s_cost_codes")
    .select("*")
    .eq("is_active", true)
    .order("code");

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("Error fetching cost codes:", error);
    throw error;
  }
  return data;
}

// ============ Assignments ============

export async function getAssignmentsByDateRange(orgId, startDate, endDate) {
  const rows = [], seen = new Set();
  for (let from = 0; ; from += 500) {
    let query = supabase.from("t4s_job_assignments")
      .select("*, employee:t4s_employees!employee_id(*), job:t4s_jobs(*)")
      .gte("assigned_date", startDate).lte("assigned_date", endDate)
      .order("assigned_date").order("id").range(from, from + 499);
    if (orgId !== "all") query = query.eq("org_id", orgId);
    const { data, error } = await query;
    if (error) throw error;
    for (const row of data || []) {
      if (seen.has(row.id)) throw new Error("Schedule changed while loading. Refresh before continuing.");
      seen.add(row.id); rows.push(row);
    }
    if ((data || []).length < 500) return rows;
  }
}

export async function createAssignment(assignmentData) {
  validateOrgId(assignmentData.org_id, "createAssignment");
  const { data, error } = await supabase
    .from("t4s_job_assignments")
    .insert(assignmentData)
    .select()
    .single();

  if (error) {
    console.error("Error creating assignment:", error);
    throw error;
  }
  return data;
}



export async function deleteAssignment(assignmentId) {
  const { error } = await supabase
    .from("t4s_job_assignments")
    .delete()
    .eq("id", assignmentId);

  if (error) {
    console.error("Error deleting assignment:", error);
    throw error;
  }
}

// Read the complete conflict snapshot, including other jobs, before review.
export async function getScheduleSnapshot(operations) {
  if (!operations.length || operations.length > 500) throw new Error("Review between 1 and 500 schedule changes at a time.");
  const employees = [...new Set(operations.map(op => op.employee_id))];
  const dates = [...new Set(operations.flatMap(op => [op.assigned_date, op.source_date].filter(Boolean)))];
  const pairs = new Set(operations.flatMap(op => [op.assigned_date, op.source_date].filter(Boolean).map(date => `${op.employee_id}/${date}`)));
  const rows = [];
  const seen = new Set();
  for (let from = 0; ; from += 500) {
    const { data, error } = await supabase.from("t4s_job_assignments")
      .select("id,org_id,job_id,employee_id,assigned_date")
      .in("employee_id", employees).in("assigned_date", dates).order("id").range(from, from + 499);
    if (error) throw error;
    for (const row of data || []) {
      if (seen.has(row.id)) throw new Error("Schedule changed during refresh. Refresh and review again.");
      seen.add(row.id);
      if (pairs.has(`${row.employee_id}/${row.assigned_date}`)) rows.push(row);
    }
    if ((data || []).length < 500) return rows;
  }
}

export async function applyScheduleBatch({ operationId, operations, expectedAssignments }) {
  const { data, error } = await supabase.rpc("t4s_apply_schedule_batch", {
    p_operation_id: operationId, p_operations: operations, p_expected_assignments: expectedAssignments,
  });
  if (error) throw error;
  return data;
}

// ============ Time Entries ============

export async function getTimeEntries(orgId, date, jobId = null) {
  return getTimeEntriesByDateRange(orgId, date, date, jobId);
}

export async function getTimeEntriesByDateRange(
  orgId,
  startDate,
  endDate,
  jobId = null,
) {
  const entries = [];
  const seen = new Set();
  const pageSize = 500;
  for (let offset = 0; ; offset += pageSize) {
    let query = supabase
      .from("t4s_time_entries")
      .select(
        `
        *,
        employee:t4s_employees!employee_id(*),
        job:t4s_jobs(*),
        allocations:t4s_time_allocations(*, cost_code:t4s_cost_codes(*))
      `,
      )
      .gte("work_date", startDate)
      .lte("work_date", endDate);

    // Only filter by org if not "all"
    if (orgId !== "all") {
      query = query.eq("org_id", orgId);
    }

    if (jobId) {
      query = query.eq("job_id", jobId);
    }

    query = query.neq("status", "removed");
    const { data, error } = await query
      .order("work_date")
      .order("id")
      .range(offset, offset + pageSize - 1);

    if (error) {
      console.error("Error fetching time entries:", error);
      throw error;
    }
    for (const entry of data || []) {
      if (seen.has(entry.id)) {
        throw new Error("Time changed while loading. Refresh before reviewing or exporting.");
      }
      seen.add(entry.id);
      entries.push(entry);
    }
    if (!data || data.length < pageSize) return entries;
  }
}

function reviewedVersions(entries) {
  if (!Array.isArray(entries) || entries.length === 0 || entries.length > 1000 ||
      entries.some(entry => !entry?.id || !entry.updated_at) ||
      new Set(entries.map(entry => entry.id)).size !== entries.length) {
    throw new Error("Refresh timecards before reviewing them (maximum 1000 entries per review).");
  }
  return entries.map(({ id, updated_at }) => ({ id, updated_at }));
}

async function reviewTimeEntries(entries, action, note = null) {
  const versions = reviewedVersions(entries);
  const { data, error } = await supabase.rpc("t4s_review_time_entries", {
    p_entries: versions, p_action: action, p_note: note,
  });
  if (error) throw error;
  if (!data || data.length !== entries.length) {
    throw new Error("Review could not be confirmed. Refresh before trying again.");
  }
  return data;
}

export const approveTimeEntries = entries => reviewTimeEntries(entries, "approved");
export const returnTimeEntries = (entries, note) => reviewTimeEntries(entries, "pending", note);

export async function updatePaidTime(entry, { clockIn, clockOut, totalHours, operationId }) {
  reviewedVersions([entry]);
  if (!operationId) throw new Error("A stable edit identity is required. Reopen the time editor.");
  const { data, error } = await supabase.rpc("t4s_review_paid_time", {
    p_operation_id: operationId,
    p_time_entry_id: entry.id,
    p_expected_updated_at: entry.updated_at,
    p_clock_in: clockIn,
    p_clock_out: clockOut,
    p_total_hours: totalHours,
  });
  if (error) throw error;
  if (data?.length !== 1 || data[0].id !== entry.id) {
    throw new Error("Time edit could not be confirmed. Refresh before trying again.");
  }
  return data[0];
}

// Reviewed RPCs have no fallback to unrestricted table updates or legacy writes.
export async function updateOpenShiftStart(entry, { clockIn, note, operationId }) {
  reviewedVersions([entry]);
  if (!operationId || !note?.trim()) throw new Error("A stable operation and correction reason are required.");
  const { data, error } = await supabase.rpc("t4s_review_open_shift_start", {
    p_operation_id: operationId, p_time_entry_id: entry.id,
    p_expected_updated_at: entry.updated_at, p_clock_in: clockIn, p_note: note.trim(),
  });
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") throw new Error("Reviewed start corrections are not available on this server yet. Your draft is preserved; ask the administrator to publish the reviewed backend before retrying.");
    throw error;
  }
  const row = data?.[0];
  if (data?.length !== 1 || row.id !== entry.id || !row.updated_at || row.clock_out != null || row.total_hours != null
      || Date.parse(row.clock_in) !== Date.parse(clockIn)
      || !["active", "pending"].includes(row.status)
      || ["org_id", "employee_id", "job_id", "work_date"].some(key => row[key] !== entry[key])) {
    throw new Error("Start correction could not be confirmed. Keep this draft and retry the same operation or reload the saved shift.");
  }
  return row;
}

export async function updateOfficeTime(entry, { clockIn, clockOut, totalHours, note, operationId }) {
  reviewedVersions([entry]);
  if (!operationId || !clockIn || !clockOut || !Number.isFinite(totalHours) || !note?.trim()) throw new Error("Review actual endpoints, explicit paid hours and a correction reason before saving.");
  const { data, error } = await supabase.rpc("t4s_review_office_time", {
    p_operation_id: operationId, p_time_entry_id: entry.id, p_expected_updated_at: entry.updated_at,
    p_clock_in: clockIn, p_clock_out: clockOut, p_total_hours: totalHours, p_note: note.trim(),
  });
  if (error) {
    if (["PGRST202", "42883"].includes(error.code)) throw new Error("Reviewed office time saves are not available on this server yet. Your draft is preserved; ask the administrator to publish the reviewed backend before retrying.");
    throw error;
  }
  const row = data?.[0];
  if (data?.length !== 1 || !row.updated_at || row.status !== "active"
      || ["id", "org_id", "employee_id", "job_id", "work_date"].some(key => row[key] !== entry[key])
      || Date.parse(row.clock_in) !== Date.parse(clockIn) || Date.parse(row.clock_out) !== Date.parse(clockOut)
      || Number(row.total_hours) !== totalHours) throw new Error("The returned office time does not match this review. Keep the draft and verify saved values before retrying.");
  return row;
}

export async function reviewAllocations(entry, allocations, allocatedBy, operationId) {
  reviewedVersions([entry]);
  if (!operationId) throw new Error("A stable allocation operation is required.");
  const payload = allocations.map(row => ({ cost_code_id: row.costCodeId, hours: row.hours }));
  if (!payload.length || payload.some(row => !row.cost_code_id || !Number.isFinite(row.hours) || row.hours <= 0)
      || new Set(payload.map(row => row.cost_code_id)).size !== payload.length) throw new Error("Review the complete allocation list before saving.");
  const { data, error } = await supabase.rpc("t4s_review_allocations", {
    p_operation_id: operationId, p_time_entry_id: entry.id, p_expected_updated_at: entry.updated_at,
    p_allocations: payload, p_allocated_by: allocatedBy,
  });
  if (error) {
    if (error.code === "PGRST202" || error.code === "42883") throw new Error("Reviewed allocation saves are not available on this server yet. Your draft is preserved; ask the administrator to publish the reviewed backend before retrying.");
    throw error;
  }
  const normalized = rows => JSON.stringify(rows.map(row => [row.cost_code_id, Number(row.hours)]).sort((a,b) => a[0].localeCompare(b[0])));
  if (!Array.isArray(data) || data.length !== payload.length || data.some(row => row.time_entry_id !== entry.id || !row.id)
      || normalized(data) !== normalized(payload)) throw new Error("Allocation response could not be confirmed. Retry the same operation or reload saved allocations.");
  return data;
}

export async function createTimeEntry(entryData) {
  validateOrgId(entryData.org_id, "createTimeEntry");
  // Validate hours
  if (entryData.total_hours != null) {
    const hours = parseFloat(entryData.total_hours);
    if (isNaN(hours) || hours < 0 || hours > 24) {
      throw new Error("Total hours must be between 0 and 24");
    }
    entryData.total_hours = hours;
  }

  // Validate clock_in < clock_out
  if (entryData.clock_in && entryData.clock_out) {
    if (new Date(entryData.clock_in) >= new Date(entryData.clock_out)) {
      throw new Error("Clock in must be before clock out");
    }
  }

  // Compare the start of the selected job-local day, not its end: checking
  // 23:59:59 incorrectly rejected every entry for the current work day.
  if (entryData.work_date) {
    const { data: job, error: jobError } = await supabase.from("t4s_jobs")
      .select("timezone").eq("id", entryData.job_id).single();
    if (jobError) throw jobError;
    const day = jobDayRange(entryData.work_date, job?.timezone);
    if (Date.parse(day.start) > Date.now()) {
      throw new Error("Work date cannot be in the future");
    }
  }

  const { data, error } = await supabase
    .from("t4s_time_entries")
    .insert(entryData)
    .select()
    .single();

  if (error) {
    console.error("Error creating time entry:", error);
    throw error;
  }
  return data;
}

export async function updateTimeEntry(entryId, updates, editedBy = null) {
  if (["clock_in", "clock_out", "total_hours"].some(key => Object.hasOwn(updates, key)) ||
      ["approved", "exported", "pending"].includes(updates.status)) {
    throw new Error("Use Timecard Review for a version-checked time correction or approval. No time was changed.");
  }
  const { data, error } = await supabase.from("t4s_time_entries")
    .update({ ...updates, ...(editedBy ? { edited_by: editedBy, edited_at: new Date().toISOString() } : {}) })
    .eq("id", entryId).select().single();
  if (error) throw error;
  return data;
}

/**
 * Remove a time entry from a daily report (soft delete).
 * Sets status to "removed" and logs to edit history for audit trail.
 */
export async function removeTimeEntry(entryId, removedBy) {
  const { data: currentEntry, error: fetchError } = await supabase
    .from("t4s_time_entries")
    .select("status, org_id")
    .eq("id", entryId)
    .single();

  if (fetchError) throw fetchError;

  const { data, error } = await supabase
    .from("t4s_time_entries")
    .update({
      status: "removed",
      edited_by: removedBy,
      edited_at: new Date().toISOString(),
    })
    .eq("id", entryId)
    .select()
    .single();

  if (error) throw error;

  if (removedBy) {
    await supabase.from("t4s_time_entry_edit_history").insert({
      time_entry_id: entryId,
      org_id: currentEntry.org_id,
      edited_by: removedBy,
      field_name: "status",
      old_value: currentEntry.status,
      new_value: "removed",
      edit_source: "manual",
    });
  }

  return data;
}

/**
 * Get edit history for a time entry
 * @param {string} timeEntryId - Time entry ID
 * @returns {Promise<Array>} List of edit history records
 */
export async function getTimeEntryEditHistory(timeEntryId) {
  const { data, error } = await supabase
    .from("t4s_time_entry_edit_history")
    .select(
      `
      *,
      editor:t4s_employees!edited_by(id, display_name, first_name, last_name)
    `,
    )
    .eq("time_entry_id", timeEntryId)
    .order("edited_at", { ascending: false });

  if (error) throw error;
  return data;
}

// Database-generated receipts are authoritative; changed_by has no FK, so
// resolve accessible employee names separately without inventing a join.
export async function getTimeEntryAuditHistory(timeEntryId, orgId) {
  validateOrgId(orgId, "getTimeEntryAuditHistory");
  const { data, error } = await supabase.from("t4s_time_entry_audit")
    .select("id,org_id,time_entry_id,action,old_clock_in,old_clock_out,new_clock_in,new_clock_out,changed_by,note,created_at,operation_payload")
    .eq("org_id", orgId).eq("time_entry_id", timeEntryId).order("created_at", { ascending: false }).limit(100);
  if (error) throw error;
  const actorIds = [...new Set((data || []).map(row => row.changed_by).filter(Boolean))];
  let actors = [];
  if (actorIds.length) {
    const result = await supabase.from("t4s_employees").select("id,display_name,first_name,last_name").in("id", actorIds);
    if (result.error) throw result.error;
    actors = result.data || [];
  }
  return (data || []).map(row => ({ ...row, editor: actors.find(actor => actor.id === row.changed_by) || null }));
}

// ============ Time Allocations ============

export async function createTimeAllocation(allocationData) {
  const { data, error } = await supabase
    .from("t4s_time_allocations")
    .insert(allocationData)
    .select()
    .single();

  if (error) {
    console.error("Error creating time allocation:", error);
    throw error;
  }
  return data;
}

export async function updateTimeAllocation(allocationId, updates) {
  const { data, error } = await supabase
    .from("t4s_time_allocations")
    .update(updates)
    .eq("id", allocationId)
    .select()
    .single();

  if (error) {
    console.error("Error updating time allocation:", error);
    throw error;
  }
  return data;
}

export async function deleteTimeAllocation(allocationId) {
  const { error } = await supabase
    .from("t4s_time_allocations")
    .delete()
    .eq("id", allocationId);

  if (error) {
    console.error("Error deleting time allocation:", error);
    throw error;
  }
}



/**
 * Save all allocations for a time entry (atomic delete + insert via Postgres RPC)
 */
export async function saveAllocations(timeEntryId, allocations, allocatedBy) {
  const payload = allocations
    .filter((a) => a.hours > 0)
    .map((a) => ({
      cost_code_id: a.costCodeId,
      hours: a.hours,
      allocated_by: allocatedBy,
    }));

  if (payload.length === 0) {
    // Just delete existing allocations
    const { error } = await supabase
      .from("t4s_time_allocations")
      .delete()
      .eq("time_entry_id", timeEntryId);
    if (error) throw error;
    return [];
  }

  const { data, error } = await supabase.rpc("t4s_save_allocations", {
    p_time_entry_id: timeEntryId,
    p_allocations: payload,
    p_allocated_by: allocatedBy,
  });

  if (error) throw error;
  return data;
}

// ============ Organizations ============

export async function getOrganization(orgId) {
  const { data, error } = await supabase
    .from("t4s_organizations")
    .select("*")
    .eq("id", orgId)
    .single();

  if (error) {
    console.error("Error fetching organization:", error);
    throw error;
  }
  return data;
}

// ============ Additional API Functions ============

export async function createShiftSplit(splitData) {
  validateOrgId(splitData.org_id, "createShiftSplit");
  const { data, error } = await supabase
    .from("t4s_shift_splits")
    .insert(splitData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getJobAssignments(jobId, date) {
  const { data, error } = await supabase
    .from("t4s_job_assignments")
    .select("*, employee:t4s_employees!employee_id(*)")
    .eq("job_id", jobId)
    .eq("assigned_date", date);
  if (error) throw error;
  return data;
}

export async function getJobCostCodes(jobId) {
  // First try direct job cost codes
  const { data, error } = await supabase
    .from("t4s_job_cost_codes")
    .select("*, cost_code:t4s_cost_codes(*)")
    .eq("job_id", jobId).order("sort_order").order("id");
  if (error) throw error;

  // If we have direct job cost codes, return them
  if (data && data.length > 0) {
    return data;
  }

  // Fall back to template-based cost codes
  const { data: job, error: jobError } = await supabase
    .from("t4s_jobs")
    .select("cost_code_template, org_id")
    .eq("id", jobId)
    .single();

  if (jobError) throw jobError;
  if (!job?.cost_code_template) {
    return [];
  }

  // Find the template by slug
  const { data: template, error: templateError } = await supabase
    .from("t4s_cost_code_templates")
    .select("id")
    .eq("org_id", job.org_id)
    .eq("slug", job.cost_code_template)
    .maybeSingle();

  if (templateError) throw templateError;
  if (!template) throw new Error("This job template is unavailable. Select valid cost codes before recording work.");

  // Get cost codes from template
  const { data: templateItems, error: itemsError } = await supabase
    .from("t4s_cost_code_template_items")
    .select("sort_order, cost_code:t4s_cost_codes(*)")
    .eq("template_id", template.id)
    .order("sort_order");

  if (itemsError) throw itemsError;

  // Return in same format as direct job cost codes
  return (templateItems || []).map((item) => ({
    id: item.cost_code.id,
    job_id: jobId,
    cost_code_id: item.cost_code.id,
    cost_code: item.cost_code,
    source: "template",
  }));
}

export function prepareJobCostCodeChange(jobId, costCodeId, action) {
  return prepareJobCostCodeMutation(supabase, { jobId, costCodeId, action });
}

export function applyJobCostCodeChange(intent) {
  return applyJobCostCodeMutation(supabase, intent);
}

export async function addCostCodeToJob(jobId, costCodeId) {
  const result = await applyJobCostCodeMutation(supabase, { jobId, costCodeId, action: "add" });
  return result.job_cost_code;
}

export async function removeCostCodeFromJob(jobId, costCodeId) {
  return applyJobCostCodeMutation(supabase, { jobId, costCodeId, action: "remove" });
}

export async function createCostCode(costCodeData) {
  validateOrgId(costCodeData.org_id, "createCostCode");
  const { data, error } = await supabase
    .from("t4s_cost_codes")
    .insert(costCodeData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateCostCode(costCodeId, updates) {
  const { data, error } = await supabase
    .from("t4s_cost_codes")
    .update(updates)
    .eq("id", costCodeId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function getJobPhotos(jobId, limit) {
  let query = supabase
    .from("t4s_job_photos")
    .select("*")
    .eq("job_id", jobId)
    .order("created_at", { ascending: false });
  if (limit) query = query.limit(limit);
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getRecentPhotos(orgId, limit = 20) {
  let query = supabase
    .from("t4s_job_photos")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name),
      taken_by_employee:t4s_employees!taken_by(id, display_name, first_name, last_name)
    `,
    )
    .order("taken_at", { ascending: false })
    .limit(limit);

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

export async function getProductionEntries(jobId, date = null) {
  let query = supabase
    .from("t4s_production_entries")
    .select("*, cost_code:t4s_cost_codes(*)")
    .eq("job_id", jobId);
  if (date) query = query.eq("work_date", date);
  const { data, error } = await query.order("created_at");
  if (error) throw error;
  return data;
}

export async function getStatusUpdates(jobId, limit = 10, day = null) {
  let query = supabase
    .from("t4s_status_updates")
    .select("*, employee:t4s_employees!posted_by(*)")
    .eq("job_id", jobId);
  if (day) {
    const { start, end } = jobDayRange(day.reportDate, day.timeZone);
    query = query.gte("created_at", start).lt("created_at", end);
  }
  const { data, error } = await query
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data;
}

// Get recent status updates across all jobs for an org
export async function getRecentUpdates(orgId, limit = 20) {
  let query = supabase
    .from("t4s_status_updates")
    .select(
      `
      *,
      posted_by:t4s_employees!posted_by(id, display_name, first_name, last_name),
      job:t4s_jobs!job_id(id, job_number, name)
    `,
    )
    .order("created_at", { ascending: false })
    .limit(limit);

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// Structured job states (on_track/delayed/blocked/complete) posted from the
// foreman app. A failed refresh must not masquerade as an empty successful feed.
export async function getRecentJobStatuses(orgId, limit = 20) {
  try {
    let query = supabase
      .from("t4s_job_status_updates")
      .select(
        `
        *,
        submitted_by_employee:t4s_employees!submitted_by(id, display_name, first_name, last_name),
        job:t4s_jobs!job_id(id, job_number, name)
      `,
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (orgId !== "all") {
      query = query.eq("org_id", orgId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (err) {
    console.warn("getRecentJobStatuses failed:", err.message);
    throw err;
  }
}

export async function getTrades(orgId) {
  if (!orgId) throw new Error("getTrades requires an orgId");
  let query = supabase.from("t4s_trades").select("*");
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }
  const { data, error } = await query
    .order("sort_order");
  if (error) throw error;
  return data;
}

export async function createTrade(tradeData) {
  validateOrgId(tradeData.org_id, "createTrade");
  const { data, error } = await supabase
    .from("t4s_trades")
    .insert(tradeData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateTrade(tradeId, updates) {
  if (Object.prototype.hasOwnProperty.call(updates, "org_id")) {
    throw new Error("A trade cannot be transferred between companies. Close this editor and reopen it in the correct company.");
  }
  const { data, error } = await supabase
    .from("t4s_trades")
    .update(updates)
    .eq("id", tradeId)
    .select()
    .single();
  if (error) throw error;
  return data;
}



const employeeAdminFields = new Set([
  "org_id", "first_name", "last_name", "phone", "email", "trade_id", "role",
  "employee_type", "is_active", "production_override", "employee_code",
  "employee_accounting_code", "notes", "avatar_color",
]);

function employeeChanges(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new Error("Employee changes must be an object.");
  }
  const changes = {};
  for (const [key, value] of Object.entries(input)) {
    if (!employeeAdminFields.has(key)) {
      throw new Error(`Employee field "${key}" cannot be changed through office administration.`);
    }
    if (value !== undefined) changes[key] = value;
  }
  if (Object.hasOwn(changes, "org_id")) validateOrgId(changes.org_id, "employee save");
  // Existing forms send the display trade slug alongside trade_id. The server
  // derives it from the verified trade instead of trusting a stale UI value.
  if (changes.trade_id) delete changes.role;
  return changes;
}

export function normalizeAssignments(input, primaryOrgId = null) {
  if (input == null) return null;
  if (!Array.isArray(input)) throw new Error("Employee organization assignments must be an array.");
  const seen = new Set();
  const assignments = input.map((assignment) => {
    const orgId = typeof assignment === "string" ? assignment : assignment?.orgId ?? assignment?.org_id;
    const isPrimary = typeof assignment === "string"
      ? orgId === primaryOrgId
      : assignment?.isPrimary ?? assignment?.is_primary ?? false;
    validateOrgId(orgId, "employee organization assignment");
    if (typeof orgId !== "string") throw new Error("An organization ID must be a string.");
    if (typeof isPrimary !== "boolean") throw new Error("The primary organization flag must be a boolean.");
    if (seen.has(orgId)) throw new Error("Duplicate employee organization assignment.");
    seen.add(orgId);
    return { org_id: orgId, is_primary: isPrimary };
  });
  if (assignments.filter((assignment) => assignment.is_primary).length !== 1) {
    throw new Error("Exactly one primary employee organization is required.");
  }
  return assignments;
}

async function employeeAdminOwner(expectedOwnerId) {
  if (typeof expectedOwnerId !== "string" || !expectedOwnerId) {
    throw new Error("The form's original signed-in account is required. Reopen the employee form before saving.");
  }
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  const ownerId = data?.session?.user?.id;
  if (!ownerId || ownerId !== expectedOwnerId) {
    throw new Error("The signed-in account changed. Reopen the employee form before saving.");
  }
  return ownerId;
}

async function saveEmployee(employeeId, updates, orgAssignments, expectedOwnerId) {
  const changes = employeeChanges(updates);
  if (!employeeId && changes.employee_type === "admin") {
    throw new Error("Creating admin privileges requires trusted server administration.");
  }
  // Updates retain employee_type, email, active state and memberships in the
  // payload. Only the RPC can distinguish unchanged admin values from a grant
  // or takeover attempt; never silently drop those protected fields.
  let primaryOrgId = changes.org_id || null;
  if (employeeId && !primaryOrgId && Array.isArray(orgAssignments)
      && orgAssignments.every((assignment) => typeof assignment === "string")) {
    // Legacy ID-only callers did not carry a primary flag. Keep the current
    // primary, rather than choosing a new home from array ordering.
    const { data: existing, error } = await supabase.from("t4s_employees")
      .select("org_id").eq("id", employeeId).single();
    await employeeAdminOwner(expectedOwnerId);
    if (error) throw error;
    if (!existing?.org_id) throw new Error("The employee's current primary organization could not be verified.");
    primaryOrgId = existing.org_id;
  }
  const assignments = normalizeAssignments(orgAssignments, primaryOrgId);
  if (assignments && changes.org_id
      && !assignments.some((assignment) => assignment.org_id === changes.org_id && assignment.is_primary)) {
    throw new Error("The primary organization must match the employee's home organization.");
  }
  await employeeAdminOwner(expectedOwnerId);
  const { data, error } = await supabase.rpc("t4s_admin_save_employee", {
    p_employee_id: employeeId,
    p_changes: changes,
    p_org_assignments: assignments,
    p_expected_owner_id: expectedOwnerId,
  });
  await employeeAdminOwner(expectedOwnerId);
  if (error) throw error;
  if (!data || Array.isArray(data) || typeof data !== "object" || typeof data.id !== "string"
      || (employeeId && data.id !== employeeId)) {
    throw new Error("The server did not confirm the employee save. Refresh before retrying.");
  }
  return data;
}

// Pass assignments here for form saves: profile and memberships then commit in
// one transaction. Omitting them preserves memberships (or creates home-only).
export async function createEmployee(employeeData, orgAssignments = null, expectedOwnerId) {
  validateOrgId(employeeData?.org_id, "createEmployee");
  const ownerId = await employeeAdminOwner(expectedOwnerId);
  return saveEmployee(null, employeeData, orgAssignments, ownerId);
}

export async function updateEmployee(employeeId, updates, orgAssignments = null, expectedOwnerId) {
  if (!employeeId) throw new Error("Employee ID is required for an update.");
  const ownerId = await employeeAdminOwner(expectedOwnerId);
  return saveEmployee(employeeId, updates, orgAssignments, ownerId);
}

async function employeeBatch(items, operation, expectedOwnerId) {
  if (!Array.isArray(items)) throw new Error("Employee batch must be an array.");
  const ownerId = await employeeAdminOwner(expectedOwnerId);
  if (items.length === 0) return [];
  const succeeded = [];
  const failed = [];
  // No automatic retries: a lost create response may already have committed.
  for (let index = 0; index < items.length; index += 1) {
    try {
      await employeeAdminOwner(ownerId);
      succeeded.push({ index, employee: await operation(items[index], ownerId) });
    } catch (error) {
      failed.push({ index, id: items[index]?.id || null, error });
    }
  }
  if (failed.length) {
    const details = failed.map(({ index, error }) => `Row ${index + 1}: ${error.message || "save failed"}`).join("; ");
    const error = new Error(`${succeeded.length} employee saves confirmed; ${failed.length} not confirmed. Refresh before retrying. ${details}`);
    error.name = "EmployeeBatchError";
    error.succeeded = succeeded;
    error.failed = failed;
    throw error;
  }
  return succeeded.map(({ employee }) => employee);
}

export async function bulkCreateEmployees(employees, expectedOwnerId) {
  return employeeBatch(employees, (employee, ownerId) => {
    validateOrgId(employee?.org_id, "bulkCreateEmployees");
    return saveEmployee(null, employee, null, ownerId);
  }, expectedOwnerId);
}

export async function bulkUpdateEmployees(updates, expectedOwnerId) {
  return employeeBatch(updates, ({ id, ...changes }, ownerId) => {
    if (!id) throw new Error("Employee ID is required for an update.");
    return saveEmployee(id, changes, null, ownerId);
  }, expectedOwnerId);
}

async function readEmployeeOrgs(employeeId, expectedOwnerId) {
  if (!employeeId) throw new Error("Employee ID is required to load organization assignments.");
  await employeeAdminOwner(expectedOwnerId);
  // A direct junction-table SELECT can silently return a home-only subset
  // under RLS. This admin RPC returns the complete set or fails closed.
  const { data, error } = await supabase.rpc("t4s_admin_get_employee_orgs", {
    p_employee_id: employeeId,
    p_expected_owner_id: expectedOwnerId,
  });
  await employeeAdminOwner(expectedOwnerId);
  if (error) throw error;
  if (!Array.isArray(data)) {
    throw new Error("The server did not confirm complete employee organization assignments.");
  }
  const seen = new Set();
  for (const assignment of data) {
    if (!assignment || typeof assignment.id !== "string"
        || assignment.employee_id !== employeeId || typeof assignment.org_id !== "string"
        || typeof assignment.is_primary !== "boolean"
        || assignment.organization?.id !== assignment.org_id
        || typeof assignment.organization?.name !== "string"
        || seen.has(assignment.org_id)) {
      throw new Error("Employee organization assignments could not be verified. Reopen the form before saving.");
    }
    seen.add(assignment.org_id);
  }
  return data;
}

export async function getEmployeeOrgs(employeeId, expectedOwnerId) {
  const ownerId = await employeeAdminOwner(expectedOwnerId);
  return readEmployeeOrgs(employeeId, ownerId);
}

export async function setEmployeeOrgs(employeeId, orgAssignments, expectedOwnerId) {
  // Compatibility for assignment-only callers. Profile forms must pass these
  // assignments to createEmployee/updateEmployee instead of a second request.
  const ownerId = await employeeAdminOwner(expectedOwnerId);
  await readEmployeeOrgs(employeeId, ownerId);
  await saveEmployee(employeeId, {}, orgAssignments, ownerId);
}

export async function updateOrganization(orgId, updates) {
  const { data, error } = await supabase
    .from("t4s_organizations")
    .update(updates)
    .eq("id", orgId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function applyTemplateToJob(jobId, templateId) {
  const { data: templateCodes, error: fetchError } = await supabase
    .from("t4s_cost_code_template_items")
    .select("cost_code_id")
    .eq("template_id", templateId);
  if (fetchError) throw fetchError;
  const inserts = templateCodes.map((tc) => ({
    job_id: jobId,
    cost_code_id: tc.cost_code_id,
  }));
  if (inserts.length > 0) {
    const { error } = await supabase
      .from("t4s_job_cost_codes")
      .upsert(inserts, { onConflict: "job_id,cost_code_id" });
    if (error) throw error;
  }
  return { applied: inserts.length };
}

// List PM4Subs companies the current user can access (RLS-scoped to their
// PM4Subs memberships). Used by the private PM4Subs settings dropdown.
export async function getPm4SubsCompanies() {
  const { data, error } = await supabase
    .from("companies")
    .select("id, name, abbreviation")
    .order("name", { ascending: true });
  if (error) throw error;
  return data || [];
}

async function assertPmLinkScope(scope) {
  const changed = () => new Error("The account or company changed. Reopen PM import/link before continuing.");
  if (!scope?.expectedOwnerId || typeof scope.isCurrent !== "function" || !scope.isCurrent()) throw changed();
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (data?.session?.user?.id !== scope.expectedOwnerId || !scope.isCurrent()) throw changed();
}

function pmLinkStatus(status, destination) {
  const statuses = destination === "office"
    ? { active: "active", "on-hold": "on_hold", completed: "completed", cancelled: "cancelled" }
    : { active: "active", on_hold: "on-hold", completed: "completed", cancelled: "cancelled" };
  return Object.hasOwn(statuses, status) ? statuses[status] : null;
}

export async function syncProjectsToJobs(orgId, scope) {
  validateOrgId(orgId, "PM import/link");
  await assertPmLinkScope(scope);
  const { data: org, error: orgError } = await supabase
    .from("t4s_organizations")
    .select("pm4subs_company_id")
    .eq("id", orgId)
    .single();
  await assertPmLinkScope(scope);
  if (orgError) throw orgError;
  if (!org?.pm4subs_company_id) throw new Error("Connect this company to PM4Subs before importing or linking jobs.");
  const { data: projects, error: projectsError } = await supabase
    .from("projects")
    .select(
      "id, name, project_number, status, location, site_latitude, site_longitude, start_date, end_date, internal_job_code, accounting_code",
    )
    .eq("company_id", org.pm4subs_company_id);
  await assertPmLinkScope(scope);
  if (projectsError) throw projectsError;
  let synced = 0;
  let skipped = 0;
  const errors = [];
  for (const project of projects || []) {
    await assertPmLinkScope(scope);
    const { data: linked, error: linkedError } = await supabase.from("t4s_jobs")
      .select("id").eq("pm4subs_project_id", project.id).maybeSingle();
    await assertPmLinkScope(scope);
    if (linkedError) { errors.push({ project: project.name, error: linkedError.message }); continue; }
    if (linked) { skipped++; continue; }
    const status = pmLinkStatus(project.status, "office");
    if (!status) { errors.push({ project: project.name, error: `Unsupported PM status: ${project.status}` }); continue; }
    const timezone = getTimezoneFromCoordinates(
      project.site_longitude,
      project.site_latitude,
    );
    const { error } = await supabase.from("t4s_jobs").insert(
      {
        org_id: orgId,
        pm4subs_project_id: project.id,
        name: project.name,
        job_number: project.project_number,
        status,
        address: project.location,
        latitude: project.site_latitude,
        longitude: project.site_longitude,
        start_date: project.start_date,
        end_date: project.end_date,
        timezone,
        internal_job_code: project.internal_job_code || null,
        accounting_code: project.accounting_code || null,
      },
    );
    await assertPmLinkScope(scope);
    if (error) {
      errors.push({ project: project.name, error: error.message });
    } else {
      synced++;
    }
  }
  return { synced, skipped, errors };
}

// Reverse sync: push t4s_jobs back to PM4Subs projects
export async function syncJobsToProjects(orgId, scope) {
  validateOrgId(orgId, "PM import/link");
  await assertPmLinkScope(scope);
  const { data: org, error: orgError } = await supabase
    .from("t4s_organizations")
    .select("pm4subs_company_id")
    .eq("id", orgId)
    .single();
  await assertPmLinkScope(scope);
  if (orgError) throw orgError;
  if (!org?.pm4subs_company_id) throw new Error("Connect this company to PM4Subs before importing or linking jobs.");

  // Get t4s_jobs that don't have a pm4subs_project_id (created in time4subs)
  const { data: t4sJobs, error: jobsError } = await supabase
    .from("t4s_jobs")
    .select("*")
    .eq("org_id", orgId)
    .is("pm4subs_project_id", null);
  await assertPmLinkScope(scope);
  if (jobsError) throw jobsError;

  let synced = 0;
  let skipped = 0;
  const errors = [];

  for (const job of t4sJobs || []) {
    await assertPmLinkScope(scope);
    // Check if a project with this number already exists in PM4Subs
    const { data: existing, error: lookupError } = await supabase
      .from("projects")
      .select("id")
      .eq("company_id", org.pm4subs_company_id)
      .eq("project_number", job.job_number)
      .maybeSingle();

    await assertPmLinkScope(scope);
    if (lookupError) { errors.push({ job: job.name, error: lookupError.message }); continue; }
    if (existing) {
      // Link the existing project and skip
      const { data: linked, error: linkError } = await supabase
        .from("t4s_jobs")
        .update({ pm4subs_project_id: existing.id })
        .eq("id", job.id).eq("org_id", orgId).is("pm4subs_project_id", null).select("id").single();
      await assertPmLinkScope(scope);
      if (linkError || !linked) errors.push({ job: job.name, error: linkError?.message || "Job link was not confirmed. Refresh before retrying." });
      else skipped++;
      continue;
    }

    const status = pmLinkStatus(job.status, "pm");
    if (!status) { errors.push({ job: job.name, error: `Unsupported office status: ${job.status}` }); continue; }
    // Create new project in PM4Subs
    const { data: newProject, error } = await supabase
      .from("projects")
      .insert({
        company_id: org.pm4subs_company_id,
        project_number: job.job_number,
        name: job.name,
        location: job.address,
        status,
        site_latitude: job.latitude,
        site_longitude: job.longitude,
        start_date: job.start_date,
        end_date: job.end_date,
        internal_job_code: job.internal_job_code || null,
        accounting_code: job.accounting_code || null,
      })
      .select("id")
      .single();

    await assertPmLinkScope(scope);
    if (error) {
      errors.push({ job: job.name, error: error.message });
    } else {
      // Link t4s_job to the new PM4Subs project
      const { data: linked, error: linkError } = await supabase
        .from("t4s_jobs")
        .update({ pm4subs_project_id: newProject.id })
        .eq("id", job.id).eq("org_id", orgId).is("pm4subs_project_id", null).select("id").single();
      await assertPmLinkScope(scope);
      if (linkError || !linked) errors.push({ job: job.name, error: "Project created, but job link was not confirmed. Refresh before retrying. " + (linkError?.message || "") });
      else synced++;
    }
  }

  return { synced, skipped, errors };
}

// ============ Job Events (Milestones) ============

export async function getUpcomingEvents(orgId, limit = 10) {
  const today = toLocalDateStr(new Date());

  let query = supabase
    .from("t4s_job_events")
    .select(
      `
      *,
      job:t4s_jobs(id, job_number, name),
      event_type:t4s_event_types(id, name),
      created_by_employee:t4s_employees!created_by(id, display_name)
    `,
    )
    .eq("status", "scheduled")
    .gte("event_date", today)
    .order("event_date", { ascending: true })
    .limit(limit);

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("organization_id", orgId);
  }

  const { data, error } = await query;

  if (error) {
    console.error("getUpcomingEvents error:", error);
    throw error;
  }

  return data;
}



export async function createJobEvent(eventData) {
  validateOrgId(eventData.organization_id, "createJobEvent");
  const { data, error } = await supabase
    .from("t4s_job_events")
    .insert(eventData)
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name),
      event_type:t4s_event_types!event_type_id(id, name)
    `,
    )
    .single();
  if (error) throw error;
  return data;
}

export async function updateJobEvent(eventId, updates) {
  const { data, error } = await supabase
    .from("t4s_job_events")
    .update(updates)
    .eq("id", eventId)
    .select()
    .single();
  if (error) throw error;
  return data;
}



export async function deleteJobEvent(eventId) {
  const { error } = await supabase
    .from("t4s_job_events")
    .delete()
    .eq("id", eventId);
  if (error) throw error;
}

export async function getEventTypes(orgId) {
  let query = supabase
    .from("t4s_event_types")
    .select("*")
    .eq("is_active", true)
    .order("sort_order", { ascending: true });

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("organization_id", orgId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// ============ Daily Reports ============

export async function getDailyReport(jobId, date) {
  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      foreman:t4s_employees!foreman_id(id, display_name, first_name, last_name),
      approved_by_employee:t4s_employees!approved_by(id, display_name)
    `,
    )
    .eq("job_id", jobId)
    .eq("report_date", date)
    .maybeSingle();
  if (error) throw error;
  return data;
}



export async function updateDailyReport(reportId, updates, expectedUpdatedAt) {
  if (!expectedUpdatedAt) throw new Error("Refresh this draft report before saving notes.");
  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .update(updates)
    .eq("id", reportId)
    .eq("status", "draft")
    .eq("updated_at", expectedUpdatedAt)
    .select()
    .single();
  if (error?.code === "PGRST116" || (!error && !data)) throw new Error("This report changed or was submitted. Refresh and review before saving notes.");
  if (error) throw error;
  return data;
}

export async function submitDailyReport(reportId, submittedBy) {
  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .update({
      status: "submitted",
      submitted_at: new Date().toISOString(),
    })
    .eq("id", reportId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function approveDailyReport(reportId, approvedBy, expectedUpdatedAt) {
  if (!expectedUpdatedAt) throw new Error("Refresh this report before approving it.");
  const { data, error } = await supabase
    .from("t4s_daily_reports")
    .update({
      status: "approved",
      approved_by: approvedBy,
      approved_at: new Date().toISOString(),
    })
    .eq("id", reportId)
    .eq("status", "submitted")
    .eq("updated_at", expectedUpdatedAt)
    .select()
    .single();
  if (error) {
    if (error.code === "PGRST116") throw new Error("This report changed or is not submitted. Refresh before approving it.");
    throw error;
  }
  if (!data) throw new Error("Report approval could not be confirmed. Refresh before trying again.");
  return data;
}

export async function getDailyReportsByDateRange(orgId, startDate, endDate, jobId = null) {
  const rows = [], seen = new Set();
  for (let from = 0; ; from += 500) {
    let query = supabase.from("t4s_daily_reports")
      .select("*, job:t4s_jobs!job_id(id, job_number, name), foreman:t4s_employees!foreman_id(id, display_name), approved_by_employee:t4s_employees!approved_by(display_name)")
      .gte("report_date", startDate).lte("report_date", endDate)
      .order("report_date", { ascending: false }).order("id").range(from, from + 499);
    if (orgId !== "all") query = query.eq("org_id", orgId);
    if (jobId) query = query.eq("job_id", jobId);
    const { data, error } = await query;
    if (error) throw error;
    for (const row of data || []) {
      if (seen.has(row.id)) throw new Error("Reports changed while loading. Refresh before continuing.");
      seen.add(row.id); rows.push(row);
    }
    if ((data || []).length < 500) return rows;
  }
}

export async function getPendingDailyReports(orgId) {
  let query = supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name),
      foreman:t4s_employees!foreman_id(id, display_name)
    `,
    )
    .in("status", ["submitted", "complete", "completed", "final"])
    .order("report_date", { ascending: false });

  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data;
}

// Get unapproved daily reports for a specific foreman
export async function getForemanUnapprovedReports(orgId, foremanId) {
  let query = supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name),
      foreman:t4s_employees!foreman_id(id, display_name, first_name, last_name)
    `,
    )
    .eq("foreman_id", foremanId)
    .in("status", ["draft", "submitted", "needs_correction", "complete", "completed", "final"])
    .order("report_date", { ascending: false });

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

// Get all unapproved daily reports (for admins)
export async function getAllUnapprovedReports(orgId) {
  let query = supabase
    .from("t4s_daily_reports")
    .select(
      `
      *,
      job:t4s_jobs!job_id(id, job_number, name),
      foreman:t4s_employees!foreman_id(id, display_name, first_name, last_name)
    `,
    )
    .in("status", ["draft", "submitted", "needs_correction", "complete", "completed", "final"])
    .order("report_date", { ascending: false });

  // Only filter by org if not "all"
  if (orgId !== "all") {
    query = query.eq("org_id", orgId);
  }

  const { data, error } = await query;

  if (error) throw error;
  return data;
}

// Fetch weather for a job and optionally save to report


// ---------------------------------------------------------------------------
// Production rates — units per bricklayer hour.
//
// Denominator rules, in order:
//   1. Hours allocated to that exact cost code    -> exact rate.
//   2. Day has NO allocations at all              -> whole-day crew hours,
//                                                    flagged approximate.
//   3. Day has allocations, but none for this code -> no rate. The hours are
//      spoken for by other codes; borrowing them would double-count and inflate.
//
// Open shifts (total_hours null) make a day's hours incomplete, so that day
// reports no rate rather than a confidently high one.
//
// Returns { rows, byCostCode, error }. `error` is non-null when a fetch failed,
// so the page can say "couldn't load" instead of blaming the crew for not
// clocking in.
// ---------------------------------------------------------------------------

// PostgREST caps a response at 1000 rows and silently truncates. Page through.
async function fetchAllRows(buildQuery, pageSize = 1000) {
  const out = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < pageSize) return out;
  }
}

export async function getProductionRates(
  orgId,
  startDate,
  endDate,
  jobId = null,
) {
  if (!orgId || !startDate || !endDate) {
    return { rows: [], byCostCode: [], error: null };
  }

  try {
    const production = await fetchAllRows(() => {
      let q = supabase
        .from("t4s_production_entries")
        .select(
          `
          id, work_date, quantity, unit, notes, job_id, cost_code_id,
          job:t4s_jobs!job_id(id, job_number, name),
          cost_code:t4s_cost_codes!cost_code_id(id, code, name, unit)
        `,
        )
        .gte("work_date", startDate)
        .lte("work_date", endDate)
        .order("work_date", { ascending: false }).order("id");
      if (orgId !== "all") q = q.eq("org_id", orgId);
      if (jobId) q = q.eq("job_id", jobId);
      return q;
    });

    // Use a narrower projection for production analysis, with the same stable pagination.
    const timeEntries = await fetchAllRows(() => {
      let q = supabase
        .from("t4s_time_entries")
        .select(
          `
          id, work_date, job_id, total_hours, status,
          employee:t4s_employees!employee_id(id, trade_id, production_override),
          allocations:t4s_time_allocations(cost_code_id, hours)
        `,
        )
        .gte("work_date", startDate)
        .lte("work_date", endDate)
        // `neq` would also drop rows whose status is NULL, since NULL != 'x'
        // is NULL in SQL. Keep them.
        .or("status.is.null,status.neq.removed")
        .order("work_date", { ascending: false }).order("id");
      if (orgId !== "all") q = q.eq("org_id", orgId);
      if (jobId) q = q.eq("job_id", jobId);
      return q;
    });

    const trades = await fetchAllRows(() => {
      let q = supabase.from("t4s_trades").select("id, name, production_default").order("id");
      if (orgId !== "all") q = q.eq("org_id", orgId);
      return q;
    });

    return aggregateProductionRates({ production, timeEntries, trades });
  } catch (err) {
    console.error("getProductionRates failed:", err);
    return {
      rows: [],
      byCostCode: [],
      error: err?.message || "Could not load production data.",
    };
  }
}

// Shared Forward Command punchlist records, read through the signed-in office session.
export async function getPunchlistItems(orgId, jobId = null) {
  return readPunchlist(supabase, orgId, jobId);
}
